import React, { useState } from 'react';
import s from './CompareTable.scss';
import classNames from 'classnames';
import { InfoIcon, Collapse, TextButton } from '@wix/design-system';
import Confirm from 'wix-ui-icons-common/Confirm';
import Minus from 'wix-ui-icons-common/Minus';
import { formatNumber } from '../../constants';

import EmailMarketing from '../../assets/icons/CompareTable/EmailMarketing.svg?url';
import Forms from '../../assets/icons/CompareTable/Forms.svg?url';
import Automations from '../../assets/icons/CompareTable/Automations.svg?url';
import SocialPost from '../../assets/icons/CompareTable/SocialPost.svg?url';
import VideoMaker from '../../assets/icons/CompareTable/VideoMaker.svg?url';
import PriceQuotes from '../../assets/icons/CompareTable/PriceQuotes.svg?url';
import Chat from '../../assets/icons/CompareTable/Chat.svg?url';

import ChevronDownSmall from 'wix-ui-icons-common/ChevronDownSmall';
import ChevronUpSmall from 'wix-ui-icons-common/ChevronUpSmall';
import { useTranslation } from '@wix/wix-i18n-config';
import { data, biLogger } from '../../globals';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';

const DisplayByProduct = (props: {
  product: { type: any; value: any; amount?: any };
  datahook: string;
  isHighlighted?: boolean;
}) => {
  const { product } = props;
  const { t } = useTranslation();

  const displayByType = () => {
    const { decimalSeparator, groupSeparator } = data.currency;
    if (product.type === 'boolean') {
      return product.value ? (
        <div data-hook="featureTrue">
          <Confirm className={s.confirm} />
        </div>
      ) : (
        <div data-hook="featureFalse">
          <Minus className={s.minus} />
        </div>
      );
    } else if (product.type === 'number') {
      return formatNumber(product.value, decimalSeparator, groupSeparator);
    } else {
      return t(product.value, {
        amount: formatNumber(product.amount, decimalSeparator, groupSeparator),
      });
    }
  };

  return (
    <div
      className={classNames(s.cell, {
        [s.mostPopularCell]: props.isHighlighted,
      })}
      data-hook={props.datahook}
    >
      {displayByType()}
    </div>
  );
};

const SingleFeature = (props: {
  feature: any;
  isUnlimitedHighlighted: boolean;
}) => {
  const { feature, isUnlimitedHighlighted } = props;
  const { t } = useTranslation();

  return (
    <div className={s.grid}>
      <div className={classNames(s.cell, s.featureTitle)}>
        <div className={s.featureName} data-hook="featureTitle">
          {t(feature.title)}
        </div>
        <InfoIcon
          size="small"
          content={t(feature.info)}
          tooltipProps={{ maxWidth: 294, placement: 'right' }}
          dataHook="featureInfoIcon"
          className={s.info}
        />
      </div>
      <DisplayByProduct
        product={feature.products.unlimited}
        isHighlighted={isUnlimitedHighlighted}
        datahook="featureByProduct-unlimited"
      />
      <DisplayByProduct
        product={feature.products.pro}
        isHighlighted={!isUnlimitedHighlighted}
        datahook="featureByProduct-pro"
      />
      <DisplayByProduct
        product={feature.products.basic}
        datahook="featureByProduct-basic"
      />
      <DisplayByProduct
        product={feature.products.free}
        datahook="featureByProduct-free"
      />
    </div>
  );
};

const SingleTool = (props: { tool: any; isUnlimitedHighlighted: boolean }) => {
  const { tool, isUnlimitedHighlighted } = props;
  const { t } = useTranslation();
  const [isCollapseOpen, setIsCollapseOpen] = useState(false);
  const featuresList: any[] = Object.entries(tool.features).map(
    ([_key, value]) => value,
  );
  featuresList.sort((a, b) => (a.order > b.order ? 1 : -1));

  const featuresListToDisplay = featuresList.slice(0, 3);
  const shouldShowSeeMoreOrLess = featuresList.length > 3;

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'EmailMarketing':
        return EmailMarketing;
      case 'Forms':
        return Forms;
      case 'Automations':
        return Automations;
      case 'SocialPost':
        return SocialPost;
      case 'VideoMaker':
        return VideoMaker;
      case 'PriceQuotes':
        return PriceQuotes;
      case 'Chat':
        return Chat;
      default:
        return '';
    }
  };

  const onShowMoreLessClicked = (ascend_product: string) => {
    const action = isCollapseOpen ? 'less' : 'more';
    setIsCollapseOpen(!isCollapseOpen);
    biLogger.showMoreLess({
      action,
      productName: 'lower_table',
      ascend_product,
    });
  };

  return (
    <div>
      <div className={classNames(s.grid, { [s.firstTool]: tool.order === 0 })}>
        <div className={classNames(s.toolHeader)}>
          {tool.icon && (
            <img
              src={getIcon(tool.icon)}
              className={s.icon}
              alt={tool.icon}
              data-hook="toolIcon"
            />
          )}
          <div className={s.toolName} data-hook="toolTitle">
            {t(tool.title)}
          </div>
        </div>
      </div>
      {featuresListToDisplay.map((feature, id) => (
        <SingleFeature
          feature={feature}
          key={id}
          isUnlimitedHighlighted={isUnlimitedHighlighted}
        />
      ))}
      {shouldShowSeeMoreOrLess && (
        <div>
          <Collapse open={isCollapseOpen}>
            {featuresList.slice(3).map((feature, id) => (
              <SingleFeature
                feature={feature}
                key={id}
                isUnlimitedHighlighted={isUnlimitedHighlighted}
              />
            ))}
          </Collapse>
          <div className={s.grid}>
            <div className={classNames(s.showMore)}>
              <TextButton
                skin="dark"
                underline="always"
                suffixIcon={
                  isCollapseOpen ? <ChevronUpSmall /> : <ChevronDownSmall />
                }
                onClick={() => onShowMoreLessClicked(tool.biName)}
                dataHook="showMore"
                size="small"
              >
                {isCollapseOpen
                  ? t('compare.toolsList.showLess')
                  : t('compare.toolsList.showMore', {
                      toolsNum: featuresList.length - 3,
                    })}
              </TextButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ToolsList = (
  props: {
    content: any[];
    isUnlimitedHighlighted: boolean;
    isHeaderSticky: boolean;
  } & InjectedExperimentsProps,
) => {
  const {
    content,
    isUnlimitedHighlighted,
    isHeaderSticky,
    experiments,
  } = props;

  const shouldHideVideoMaker = experiments.enabled(
    'specs.ascend.PackagePickerRemoveVideoMaker',
  );

  const filteredContent = content.filter(
    (contentItem) =>
      !shouldHideVideoMaker || !contentItem.title.includes('videoMaker'),
  );

  return (
    <div
      className={classNames({ [s.compensateStickyHeader]: isHeaderSticky })}
      data-hook="toolsList"
    >
      {filteredContent.map((tool, id) => (
        <SingleTool
          tool={tool}
          key={id}
          isUnlimitedHighlighted={isUnlimitedHighlighted}
        />
      ))}
    </div>
  );
};

export default withExperiments(ToolsList);
